<template>
  <div class="flex justify-between items-center">
    <div class="flex gap-4">
      <BittsQuickFilter
        v-model="filterValues.mapping_status"
        :filter="mappingStatusFilter"
        :open-on-mount="false"
        :show-search="false"
        :disabled="isActionBarDisabled"
        size="medium"
        :options="mappingStatusFilter.options"
        @clear-filter="handleClearFilter"
        @apply-filter="handleApplyFilter"
        @remove-filter="handleRemoveFilter"
      />
    </div>
    <div class="flex gap-6">
      <BittsInput
        v-model="searchValue"
        name="search"
        :placeholder="searchPlaceholder"
        type="search"
        size="small"
        :disabled="isActionBarDisabled"
        @update:model-value="handleSearch"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { BittsInput, BittsQuickFilter } from '@crossbeam/bitts';
import { BittsIcon } from '@crossbeam/bitts/src/types';

import { useDebounceFn } from '@vueuse/core';
import { computed, reactive, ref } from 'vue';

type FilterOption = {
  label: string;
  value: string | number;
};

type FilterValues = Record<string, (string | number)[]>;

type Props = {
  isActionBarDisabled?: boolean;
  searchPlaceholder?: string;
};

withDefaults(defineProps<Props>(), {
  isActionBarDisabled: false,
  searchPlaceholder: 'Search partners',
});

const emit = defineEmits<{
  'search-applied': [value: string];
  'filters-applied': [filterValues: FilterValues];
}>();

const mappingStatusFilter = computed(() => ({
  value: 'mapping_status',
  label: 'Mapping Status',
  faIcon: ['fak', 'mapping'] as BittsIcon,
  options: [
    { label: 'Mapped', value: 'mapped' },
    { label: 'Unmapped', value: 'unmapped' },
  ] as FilterOption[],
}));

const filterValues = reactive<FilterValues>({
  mapping_status: [],
});

const searchValue = ref('');

const debouncedSearch = useDebounceFn((value: string) => {
  emit('search-applied', value);
}, 300);

function handleSearch(value: string) {
  debouncedSearch(value);
}

function handleClearFilter() {
  filterValues.mapping_status = [];
  emit('filters-applied', { ...filterValues });
}

function handleRemoveFilter() {
  filterValues.mapping_status = [];
  emit('filters-applied', { ...filterValues });
}

function handleApplyFilter() {
  emit('filters-applied', { ...filterValues });
}

function clearAllFilters() {
  filterValues.mapping_status = [];
  searchValue.value = '';
  emit('filters-applied', { ...filterValues });
  emit('search-applied', '');
}

defineExpose({
  clearAllFilters,
});
</script>
